import { useNavigate } from 'react-router-dom'
import { Box, Typography, Button, Chip } from '@mui/material'
import { ArrowBackIos as ArrowBackIcon, KeyboardArrowDown } from '@mui/icons-material'

// others
import { BillStatus, FetchBillDetailsQuery } from '../../../../generated'
import { canEditFundRequest, canViewFundRequestActions, formatStatus, statusColors } from '../../../utils/billing'
import { useContext, useState } from 'react'
import { AuthContext } from '../../../context'
import { useReactToPrint } from 'react-to-print'
import { Alert } from '../../common/Alert'
import EditFundRequestModal from '../fundRequests/modal/EditFundRequestModal'
import { FundRequestMenu } from '../../menus/FundRequest'

interface FundRequestDetailHeaderProps {
  bill?: FetchBillDetailsQuery['fetchBillDetails']['bill']
  clientId?: string
  clientPayableAmount?: number
  onRefetch: () => void
}

const FundRequestDetailHeader = ({ bill, clientId, clientPayableAmount, onRefetch }: FundRequestDetailHeaderProps) => {
  const [isEditFundRequestModalOpen, setIsEditFundRequestModalOpen] = useState(false)
  const { currentUser } = useContext(AuthContext)
  const navigate = useNavigate()
  const [isPrinting, setIsPrinting] = useState(false)

  const { id, status } = bill || {}
  const canEdit = bill?.status && currentUser && canEditFundRequest(bill?.status, currentUser)
  const showBillActions = bill?.status && currentUser && canViewFundRequestActions(bill.status, currentUser)

  const handleBack = () => {
    navigate('/billing/fund-requests')
  }

  const handleDownloadPDF = useReactToPrint({
    content: () => document.getElementById('bill-preview'),
    documentTitle: `Bill_IN${id}`,
    onBeforePrint: () => {
      setIsPrinting(true)
    },
    onAfterPrint: () => {
      if (isPrinting) {
        Alert.success('Bill downloaded successfully')
        setIsPrinting(false)
      }
    },
    onPrintError: () => {
      setIsPrinting(false)
      Alert.error('Failed to download bill')
    }
  })

  const handleEdit = () => {
    if (typeof id === 'number') {
      setIsEditFundRequestModalOpen(true)
    }
  }

  const getStatus = (status: BillStatus) => {
    if (status === BillStatus.Billed && bill?.sendToClient) {
      return 'Ready to send'
    }

    return formatStatus(status)
  }

  return (
    <Box mb={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Button
          startIcon={<ArrowBackIcon sx={{ width: '13px' }} />}
          sx={{
            color: '#5B5675',
            fontWeight: 600,
            fontSize: '14px',
            ml: -1
          }}
          onClick={handleBack}
        >
          BACK
        </Button>

        {bill && (
          <Box display="flex" alignItems="center" gap={2}>
            {showBillActions && (
              <FundRequestMenu
                hideViewOption
                bill={bill}
                caseId={bill.case?.id}
                billingType={bill.case?.billing?.billingType}
                account={bill.account}
                clientId={clientId}
                clientPayableAmount={clientPayableAmount}
                onRefetch={onRefetch}
                renderAction={handleClick => (
                  <Button variant="outlined" endIcon={<KeyboardArrowDown />} onClick={handleClick}>
                    Actions
                  </Button>
                )}
              />
            )}

            <Button variant="outlined" onClick={handleDownloadPDF}>
              DOWNLOAD CLIENT RECEIPT
            </Button>

            {canEdit && (
              <Button variant="contained" onClick={handleEdit}>
                EDIT
              </Button>
            )}
          </Box>
        )}
      </Box>

      <Box display="flex" alignItems="center" gap={2} mt={1}>
        <Typography variant="h6" fontWeight={500} color="#333">
          #IN{id}
        </Typography>
        {status && (
          <Chip
            label={getStatus(status)}
            sx={{
              color: statusColors[status],
              border: `1px solid ${statusColors[status]}40`,
              backgroundColor: 'transparent'
            }}
          />
        )}
      </Box>

      {isEditFundRequestModalOpen && typeof id === 'number' && (
        <EditFundRequestModal
          billId={id}
          isOpen={isEditFundRequestModalOpen}
          onClose={() => setIsEditFundRequestModalOpen(false)}
          onSuccess={onRefetch}
        />
      )}
    </Box>
  )
}

export default FundRequestDetailHeader
